import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./calendario.css";
import CalendarioCell from "./calendarioCell";
import type { RangeSelection } from "./calendarCreateModal";
import {
  type CalendarCellData,
  type CalendarRow,
  type CalendarEventItem,
  type CellThirdKey,
} from "./useCalendarioLogic";
import CalendarioMetaRow from "./calendarioMetaRow";
import type { MetaField } from "./MetaEditModal";
import type { StaysCalendarMeta } from "./useCalendarioLogic";
import {
  CaretRightOutlined,
  CaretDownOutlined,
  MoreOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import airbnbIcon from "../../assets/logos/airbnb.svg";
import bookingIcon from "../../assets/logos/bookingcom.svg";
import despegarIcon from "../../assets/logos/Despegar.png";
import staysIcon from "../../assets/logos/stays.png";
import { Dropdown, Grid, theme, Tooltip, type MenuProps } from "antd";
// La misma detección de teléfono que usa el Trigger de antd (ver BarraCalendario).
import isMobile from "rc-util/lib/isMobile";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { formatEstadiaCode } from "../../utils/estadiaCode";
import {
  type MercadoPaleta,
  colorDeOcupacion,
  textoContraste,
  nivelDeOcupacion,
  nivelLabel,
} from "../../utils/mercadoPaletas";
import type { MercadoCalendarioResumenItem, MercadoResumenCombo } from "../../services/mercado.api";

const PLATFORM_LOGO: Record<string, string> = {
  airbnb: airbnbIcon,
  booking: bookingIcon,
  despegar: despegarIcon,
  stays: staysIcon,
};

type RangeMeta = {
  propertyId: number;
  startIdx: number;
  endIdx: number;
  startThird?: CellThirdKey;
  endThird?: CellThirdKey;
};

const normalizePlatform = (name?: string) => (name ?? "").toLowerCase().trim();

// Formato CLP para las celdas de precio (base / efectivo): separador de miles
// con punto, sin decimales. Devuelve "-" si no hay valor.
const formatClp = (v: any) => {
  const n = Number(v);
  if (v == null || !Number.isFinite(n)) return "-";
  return `$${Math.round(n).toLocaleString("es-CL")}`;
};

// Tercios equidistantes gráficamente (1/3 cada uno) aunque los intervalos
// horarios NO son proporcionales: morning 00:00–10:59 (11h), mid 11:00–14:59
// (4h), evening 15:00–23:59 (9h). Los cortes visuales caen en 11:00 y 15:00.
// Una barra que cruza la noche (ej: 15:00 día A → 11:00 día B) va desde 2/3
// del día A hasta 1/3 del día B.
const THIRD = {
  morningEnd: 1 / 3,
  midStart: 1 / 3,
  midEnd: 2 / 3,
  eveningStart: 2 / 3,
};

function formatHeader(date: string, _isFirst: boolean) {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const weekday = dt.toLocaleDateString("es-CL", { weekday: "short" });
  const month = dt.toLocaleDateString("es-CL", { month: "short" });
  const showMonth = true;
  return { day: d, weekday, month, showMonth };
}


function toISODateLocal(d: Date) {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function minutesOfDay(d: Date) {
  return d.getHours() * 60 + d.getMinutes();
}

/** Fracción X de inicio según hora (se cuantiza a tercios) */
function startFracForMinutes(min: number) {
  if (min < 11 * 60) return 0.0;
  if (min < 15 * 60) return THIRD.midStart;
  return THIRD.eveningStart;
}
/** Fracción X de término según hora (se cuantiza a tercios) */
function endFracForMinutes(min: number) {
  if (min <= 0) return 0.0;
  if (min <= 11 * 60) return THIRD.morningEnd;
  if (min <= 15 * 60) return THIRD.midEnd;
  return 1.0;
}

type BarMenuActions = {
  onViewFinanzas?: (propertyId: number, date: string, bookingId?: number) => void;
  onEditBooking?: (booking: any, propertyId?: number) => void;
  onEditEvent?: (event: CalendarEventItem) => void;
  // Eliminación de arriendo. `preserveCleaning=true` desvincula el aseo
  // AUTO (reserva_id=null) en vez de borrarlo — útil cuando el aseo ya se
  // ejecutó. Ver docs/calendario-cascada-arriendos.md §2 y el dropdown
  // equivalente en bookingDetailModal.tsx.
  onDeleteBooking?: (
    bookingId: number,
    opts?: { preserveCleaning?: boolean },
  ) => void;
  onDeleteEvent?: (event: CalendarEventItem) => void;
  onCopyInfo?: (booking: any) => void;
  onCopyInstructions?: (propertyId: number) => void;
};

type Props = {
  calendarRows: CalendarRow[];
  dates: string[];
  getCell: (row: CalendarRow, date: string) => CalendarCellData;
  todayISO: string;
  onCellClick?: (
    cell: CalendarCellData,
    third: CellThirdKey,
    e: React.MouseEvent,
  ) => void;
  onCellDoubleClick?: (
    cell: CalendarCellData,
    third: CellThirdKey,
    e: React.MouseEvent,
  ) => void;
  onCellContextMenu?: () => void;
  rangeSelection?: RangeSelection | null;
  barMenuActions?: BarMenuActions;
  wrapperRef?: React.RefObject<HTMLDivElement>;
  onWrapperScroll?: () => void;
  onMetaRangeComplete?: (
    field: MetaField,
    propertyId: number,
    startDate: string,
    endDate: string,
  ) => void;
  staysCalendarData?: Record<number, StaysCalendarMeta>;
  fetchPropertyMeta?: (propertyId: number) => Promise<void>;
  /** Sólo admin puede editar la meta (precio/condición/min noches/restricción). */
  canEditMeta?: boolean;
  /**
   * Ids de propiedades cuya meta se está REFRESCANDO tras aplicar un cambio
   * (PATCH ok, esperando la re-lectura con los definitivos). Sus filas meta
   * muestran spinner + pulso hasta que llegan los valores nuevos. Round
   * 2026-07-02.
   */
  metaRefreshingIds?: Set<number>;
  /** Geo + cliente por propiedad para el subtexto bajo el nombre en la cabecera. */
  propertyInfoById?: Record<
    number,
    {
      regionNombre?: string | null;
      comunaNombre?: string | null;
      sectorNombre?: string | null;
      clienteNombre?: string | null;
    }
  >;
  /**
   * Fila informativa de MERCADO (admin, round 2026-07-24): si `mercadoEnabled`,
   * bajo cada propiedad se dibuja una fila extra "Mercado · NOMBRE" con el
   * relleno de ocupación (a la paleta `mercadoPaleta`) + banda de precio + una
   * flecha que compara el precio base actual (si la meta ya está cargada) con la
   * banda recomendada. Es SOLO informativa (sin interacción). Los datos vienen
   * batcheados por rango en `mercadoByProperty`.
   */
  mercadoEnabled?: boolean;
  mercadoPaleta?: MercadoPaleta;
  mercadoByProperty?: Record<number, MercadoCalendarioResumenItem>;
};

/** Formatea una banda de precio a "45-65k" (miles). Colapsa a "55k" si desde=hasta. */
function fmtBandaMiles(banda: { desde: number; hasta: number }): string {
  const k = (n: number) => Math.round(n / 1000);
  const lo = k(banda.desde);
  const hi = k(banda.hasta);
  return lo === hi ? `${lo}k` : `${lo}-${hi}k`;
}

/** Precio base representativo de un conjunto de fechas (promedio de las noches
 * con base cargada). null si la meta aún no se cargó (propiedad sin expandir). */
function baseRepDelRango(
  staysData: StaysCalendarMeta | undefined,
  dates: string[],
): number | null {
  if (!staysData?.base) return null;
  let suma = 0;
  let n = 0;
  for (const d of dates) {
    const v = Number(staysData.base[d]);
    if (Number.isFinite(v) && v > 0) {
      suma += v;
      n += 1;
    }
  }
  return n > 0 ? Math.round(suma / n) : null;
}

/** Un tramo contiguo de días del rango visible cubierto por un mismo combo
 * scrapeado (estadía representante). */
interface MercadoSegmento {
  startIdx: number;
  endIdx: number;
  combo: MercadoResumenCombo;
}

/**
 * Parte el rango visible en segmentos, uno por combo scrapeado. Cada día se
 * asigna al combo que lo cubre (`[checkin, checkout)`, checkout excluido) con
 * MAYOR ocupación (representante, igual que el módulo Mercado). Los días
 * contiguos con el mismo combo se agrupan en un segmento. Días sin combo quedan
 * como hueco (sin segmento). Así el precio/ocupación que se muestra pertenece al
 * RANGO SCRAPEADO, no al día ni al rango visible completo.
 */
function buildMercadoSegments(
  combos: MercadoResumenCombo[],
  dates: string[],
): MercadoSegmento[] {
  if (!combos.length) return [];
  const rep: (MercadoResumenCombo | null)[] = dates.map((d) => {
    let best: MercadoResumenCombo | null = null;
    for (const c of combos) {
      if (c.checkin <= d && d < c.checkout) {
        if (!best || c.ocupacion > best.ocupacion) best = c;
      }
    }
    return best;
  });
  const segs: MercadoSegmento[] = [];
  let i = 0;
  while (i < dates.length) {
    const c = rep[i];
    if (!c) {
      i += 1;
      continue;
    }
    const key = `${c.checkin}|${c.checkout}`;
    let j = i;
    while (j + 1 < dates.length) {
      const nx = rep[j + 1];
      if (nx && `${nx.checkin}|${nx.checkout}` === key) j += 1;
      else break;
    }
    segs.push({ startIdx: i, endIdx: j, combo: c });
    i = j + 1;
  }
  return segs;
}

const thirdOrder = (t: CellThirdKey) =>
  t === "morning" ? 0 : t === "mid" ? 1 : 2;

function maskBetween(a: CellThirdKey, b: CellThirdKey) {
  const ao = thirdOrder(a);
  const bo = thirdOrder(b);
  const lo = Math.min(ao, bo);
  const hi = Math.max(ao, bo);
  let m = 0;
  if (lo <= 0 && hi >= 0) m |= 1;
  if (lo <= 1 && hi >= 1) m |= 2;
  if (lo <= 2 && hi >= 2) m |= 4;
  return m;
}

const THIRD_ORDER: CellThirdKey[] = ["morning", "mid", "evening"];

const maskFromThirdToEnd = (t: CellThirdKey) => {
  // morning->111, mid->110, evening->100
  const i = THIRD_ORDER.indexOf(t);
  return (i <= 0 ? 1 : 0) | (i <= 1 ? 2 : 0) | 4;
};

const maskFromStartToThird = (t: CellThirdKey) => {
  // morning->001, mid->011, evening->111
  const i = THIRD_ORDER.indexOf(t);
  return 1 | (i >= 1 ? 2 : 0) | (i >= 2 ? 4 : 0);
};

function selectionMaskForDate(range: RangeMeta, idx: number) {
  const startThird = range.startThird ?? "morning";
  const endThird = range.endThird ?? "evening";

  if (idx < range.startIdx || idx > range.endIdx) return 0;

  // mismo día
  if (range.startIdx === range.endIdx) return maskBetween(startThird, endThird);

  // primer día del rango
  if (idx === range.startIdx) return maskFromThirdToEnd(startThird);

  // último día del rango
  if (idx === range.endIdx) return maskFromStartToThird(endThird);

  // días intermedios
  return 1 | 2 | 4; // 7
}

type CellHandler = (
  cell: CalendarCellData,
  third: CellThirdKey,
  e: React.MouseEvent,
) => void;

// ─────────────────────────────────────────────────────────────────────────────
// Memoización de la grilla (round 2026-09-11).
//
// Con rangos de hasta 14 meses (~427 días) la grilla dibuja una CalendarioCell
// por día y por track. Antes, cada render de calendario.tsx (un clic de
// selección, un tick de WebSocket) re-renderizaba TODAS las celdas de TODAS las
// filas, porque los handlers llegaban nuevos en cada render, un día vacío
// recibía un objeto celda nuevo en cada llamada a getCell y la selección se
// leía en cada celda. Las piezas de abajo reciben solo props estables, así que
// un cambio de selección re-renderiza únicamente las filas de la propiedad que
// la pierde y la que la gana.
// ─────────────────────────────────────────────────────────────────────────────

type FilaCeldasProps = {
  row: CalendarRow;
  dates: string[];
  gridTemplateColumns: string;
  todayISO: string;
  getCell: (row: CalendarRow, date: string) => CalendarCellData;
  /**
   * Selección SOLO si es de la propiedad de esta fila; para las demás filas el
   * padre pasa null, así su prop no cambia cuando la selección se mueve.
   */
  seleccion: RangeMeta | null;
  /** Handlers ESTABLES (ver los wrappers por ref en CalendarioGrid). */
  onCellClick: CellHandler;
  onCellDoubleClick: CellHandler;
  onCellContextMenu: () => void;
};

/** Las celdas de UN track: el `.cal-dates-grid` con una CalendarioCell por día. */
const FilaCeldas = memo(function FilaCeldas({
  row,
  dates,
  gridTemplateColumns,
  todayISO,
  getCell,
  seleccion,
  onCellClick,
  onCellDoubleClick,
  onCellContextMenu,
}: FilaCeldasProps) {
  // getCell devuelve un objeto NUEVO para cada día vacío (defaultEmptyCell).
  // Calcularlas una vez por fila deja cada celda con la misma referencia
  // mientras la fila no cambie; así la CalendarioCell memoizada no se invalida.
  // Nadie muta este objeto: calendario.tsx solo lee idPropiedad/date y arma el
  // detalle con mergeCellsForPropertyDay, que construye un objeto propio.
  const celdas = useMemo(
    () => dates.map((date) => getCell(row, date)),
    [row, dates, getCell],
  );
  // Guarda defensiva: aunque llegue la selección de otra propiedad, no se pinta.
  const sel =
    seleccion && seleccion.propertyId === row.propertyId ? seleccion : null;

  return (
    <div className="cal-dates-grid" style={{ gridTemplateColumns }}>
      {dates.map((date, idx) => {
        // `idx` es la posición del día en `dates`: la misma que daba antes
        // dateToIdx.get(date), porque las fechas del rango son únicas.
        const selectedMask = sel ? selectionMaskForDate(sel, idx) : 0;
        return (
          <CalendarioCell
            key={`${row.propertyId}-${row.trackIndex}-${date}`}
            cell={celdas[idx]}
            isToday={date === todayISO}
            selectedMask={selectedMask}
            isStartDay={!!sel && idx === sel.startIdx}
            isEndDay={!!sel && idx === sel.endIdx}
            // CalendarioCell no lee startThird/endThird (quedan en sus Props
            // por compatibilidad). Van solo a los días dentro del rango: si
            // fueran a todos, cambiar el tercio de la selección invalidaría
            // cada celda memoizada de la fila sin cambiar nada visible.
            startThird={selectedMask ? sel?.startThird : undefined}
            endThird={selectedMask ? sel?.endThird : undefined}
            onClick={onCellClick}
            onDoubleClick={onCellDoubleClick}
            onContextMenu={onCellContextMenu}
          />
        );
      })}
    </div>
  );
});

// ─── Barras de arriendos y eventos (round 2026-09-11) ────────────────────────
//
// Antes, cada barra se re-renderizaba en cada render de calendario.tsx por dos
// motivos: barMenuActions llega como objeto nuevo en cada render (deleteEvent se
// redeclara) y el menú abierto (openMenuKey) lo leían todas las filas.
// FilaBarras recibe solo props estables: la fila, la geometría (cambia solo con
// dates o el ancho de celda), invocadores de acciones con identidad fija, la
// disponibilidad del menú como booleano y el menú abierto SOLO si es de esa
// fila. Así una selección, abrir o cerrar un menú o un re-render del padre con
// los mismos datos re-renderizan únicamente las filas afectadas.
//
// Tooltip y Dropdown a pedido (round 2026-09-14). Cada barra montaba al cargar
// su Tooltip y su Dropdown de antd, y con unas 150 barras por propiedad ese
// montaje (hooks de rc-trigger y de @ant-design/cssinjs por instancia) era la
// mayor parte del primer pintado de un rango largo. Ahora la barra se dibuja con
// los mismos elementos de siempre y monta el Tooltip recién cuando el puntero
// lleva sobre ella el mismo retraso de antes, y el Dropdown solo mientras su
// menú está abierto (o cerrándose). Ver BarraCalendario.

/**
 * Clave del menú (⋮) de la barra de un arriendo. La usan la barra y el cálculo
 * de qué filas tienen el menú abierto: si divergen, el menú no se abre.
 */
const claveMenuReserva = (b: any) => `bk-${b.id}`;
/** Clave del menú (⋮) de la barra de un evento (aseo, bloqueo o mantención). */
const claveMenuEvento = (ev: CalendarEventItem) => `ev-${ev.kind}-${ev.id}`;

/** Deriva la fecha de la columna clickeada a partir del clientX dentro de una barra. */
function dateFromClickX(
  e: React.MouseEvent,
  dates: string[],
  cellW: number,
): string {
  const track = (e.currentTarget as HTMLElement).closest(".cal-bars-track");
  if (track) {
    const rect = track.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const idx = Math.min(
      Math.max(Math.floor(relX / cellW), 0),
      dates.length - 1,
    );
    return dates[idx];
  }
  return dates[0];
}

/**
 * Deriva el TERCIO (morning/mid/evening) de una barra a partir del
 * clientX del click. Necesario para que bares angostas (ej: un bloqueo
 * que cubre sólo el tercio mid) pasen el `third` CORRECTO al
 * onCellClick. Antes estaba hardcodeado a "mid"/"evening" y en bares
 * cuyo tercio real era morning se disparaba range-select en vez de
 * abrir el detalle, impidiendo editar/eliminar la barra.
 *
 * Mismos cortes gráficos 1/3 · 2/3 definidos en THIRD.
 */
function thirdFromClickX(
  e: React.MouseEvent,
  dates: string[],
  cellW: number,
): CellThirdKey {
  const track = (e.currentTarget as HTMLElement).closest(".cal-bars-track");
  if (!track) return "mid";
  const rect = track.getBoundingClientRect();
  const relX = e.clientX - rect.left;
  const idx = Math.min(
    Math.max(Math.floor(relX / cellW), 0),
    dates.length - 1,
  );
  const frac = (relX - idx * cellW) / cellW; // 0..1 dentro del cell
  if (frac < THIRD.morningEnd) return "morning";
  if (frac < THIRD.midEnd) return "mid";
  return "evening";
}

/**
 * Geometría horizontal de las barras. CalendarioGrid la deriva de `dates` y del
 * ancho de celda, así que no cambia mientras no cambien esos dos.
 */
type GeometriaBarras = {
  minDate: string;
  maxDate: string;
  dateIndexMap: Map<string, number>;
  cellW: number;
  totalWidth: number;
};

function calcBarPosition(start: Date, end: Date, geo: GeometriaBarras) {
  const { minDate, maxDate, dateIndexMap, cellW, totalWidth } = geo;
  const sDate = toISODateLocal(start);
  const eDate = toISODateLocal(end);

  if (eDate < minDate) return null;
  if (sDate > maxDate) return null;

  const startsBefore = sDate < minDate;
  const endsAfter = eDate > maxDate;

  let startX = 0;
  let endX = totalWidth;

  if (!startsBefore) {
    const idx = dateIndexMap.get(sDate);
    if (idx === undefined) return null;
    startX = idx * cellW + startFracForMinutes(minutesOfDay(start)) * cellW;
  }
  if (!endsAfter) {
    const idx = dateIndexMap.get(eDate);
    if (idx === undefined) return null;
    endX = idx * cellW + endFracForMinutes(minutesOfDay(end)) * cellW;
  }

  const width = Math.max(0, endX - startX);
  if (width < 2) return null;

  return { startX, width, flatLeft: startsBefore, flatRight: endsAfter };
}

/**
 * Invocadores ESTABLES de barMenuActions: siempre definidos, cada uno llama a
 * la acción de la ÚLTIMA versión de la prop (o no hace nada si ya no existe).
 */
type AccionesBarra = Required<BarMenuActions>;

/** Contenido del Tooltip de la barra de un arriendo. */
function contenidoTooltipReserva(b: any, ingreso: string, salida: string) {
  return (
    <div className="cal-tooltip-content">
      <div className="cal-tooltip-title">
        {b.nombreHuesped ?? "—"}
      </div>
      <div className="cal-tooltip-row">
        <span>Total reserva</span>
        <strong>
          $
          {Number(b.reservaTotalClp || 0).toLocaleString(
            "es-CL",
          )}
        </strong>
      </div>
      <div className="cal-tooltip-row">
        <span>Precio/noche</span>
        <strong>
          $
          {Number(b.precioNochesClp || 0).toLocaleString(
            "es-CL",
          )}
        </strong>
      </div>
      {ingreso && salida && (
        <div className="cal-tooltip-row">
          <span>Fechas</span>
          <span>
            {ingreso.split("-").reverse().join("-")} →{" "}
            {salida.split("-").reverse().join("-")}
          </span>
        </div>
      )}
      {/*
        «Ref. plataforma» = código corto de la plataforma
        (`id_reservac`, formato JS11J). Se muestra SOLO cuando la
        reserva vino de Stays/Airbnb/Booking; sirve para cruzarla
        con el panel /stays-webhooks. El identificador universal
        de toda reserva (manual o de plataforma) es «Estadía #N»,
        que se muestra siempre abajo. El AF-… (numero_corto) quedó
        legacy — round 2026-07-20.
      */}
      {b.idReservaStays && (
        <div className="cal-tooltip-row">
          <span>Ref. plataforma</span>
          <strong>{b.idReservaStays}</strong>
        </div>
      )}
      {/* Código de estadía AF (booking.id) — identificador
          único y estable; el mismo #N que muestra el aseo
          vinculado y la tabla de arriendos. */}
      {b.id != null && (
        <div className="cal-tooltip-row">
          <span>Estadía</span>
          <strong>{formatEstadiaCode(b.id)}</strong>
        </div>
      )}
      {b.fechaCreacion && (
        <div className="cal-tooltip-row">
          <span>Creada</span>
          <span>
            {new Date(b.fechaCreacion).toLocaleString("es-CL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      )}
    </div>
  );
}

/** Contenido del Tooltip de la barra de un evento (aseo, bloqueo o mantención). */
function contenidoTooltipEvento(ev: CalendarEventItem) {
  return ev.kind === "cleaning" ? (
    <div className="cal-tooltip-content">
      <div className="cal-tooltip-title">
        Aseo
        {(ev as any).reservaId ? (
          <span className="cal-tooltip-badge">AUTO</span>
        ) : null}
      </div>
      {/* Aseo AUTO vinculado a una reserva: dejamos
          explícito a quién corresponde para que el admin
          identifique al toque la estadía (especialmente
          útil cuando hay varios aseos el mismo día en la
          misma propiedad). Round 2026-05-30. */}
      {(ev as any).huespedReserva && (
        <div className="cal-tooltip-row">
          <span>Reserva de</span>
          <strong>{(ev as any).huespedReserva}</strong>
        </div>
      )}
      {/* Código de aseo propio (aseos.id = ev.id) — único
          e histórico, distinto del código de estadía. */}
      {ev.id != null && (
        <div className="cal-tooltip-row">
          <span>Aseo</span>
          <strong>{formatEstadiaCode(ev.id)}</strong>
        </div>
      )}
      {/* Código de estadía AF del arriendo vinculado — el
          MISMO #N que muestra el arriendo, para cruzarlos. */}
      {(ev as any).reservaId != null && (
        <div className="cal-tooltip-row">
          <span>Estadía</span>
          <strong>
            {formatEstadiaCode((ev as any).reservaId)}
          </strong>
        </div>
      )}
      <div className="cal-tooltip-row">
        <span>Aseador</span>
        <strong>{ev.aseadorNombre || "Sin asignar"}</strong>
      </div>
      <div className="cal-tooltip-row">
        <span>Urgencia</span>
        <span>{ev.urgencia || "—"}</span>
      </div>
      {ev.notas && (
        <div className="cal-tooltip-row">
          <span>Notas</span>
          <span>{ev.notas}</span>
        </div>
      )}
    </div>
  ) : (
    <div className="cal-tooltip-content">
      <div className="cal-tooltip-title">
        {ev.kind === "maintenance"
          ? "Mantención"
          : "Bloqueo"}
        <span className="cal-tooltip-badge">
          {/*
            Round 2026-05-29 fase 4 — tres orígenes posibles
            para un bloqueo, no dos:
              - true   → admin/superadmin desde la UI
              - false  → cliente (dueño) desde la UI
              - null   → webhook de Stays (consulta/inquiry
                         o sync de calendario externo).
                         Antes esto se guardaba como false
                         y mostraba "Hecho por cliente" —
                         confundía porque ningún cliente
                         lo había hecho.
          */}
          {(ev as any).createdByAdmin === true
            ? "Hecho por admin"
            : (ev as any).createdByAdmin === false
              ? "Hecho por cliente"
              : "Desde Stays"}
        </span>
      </div>
      <div className="cal-tooltip-row">
        <span>Razón</span>
        <strong>{ev.razon || "—"}</strong>
      </div>
      {(ev as any).notas ? (
        <div className="cal-tooltip-row cal-tooltip-notes">
          <span>Notas</span>
          <span>{(ev as any).notas}</span>
        </div>
      ) : null}
    </div>
  );
}

/** Ítems del menú (⋮) de la barra de un arriendo. */
function itemsMenuReserva(
  b: any,
  propertyId: number,
  ingreso: string,
  acciones: AccionesBarra,
): MenuProps["items"] {
  return [
    {
      key: "finanzas",
      label: "Ver en finanzas",
      onClick: () =>
        acciones.onViewFinanzas(
          propertyId,
          ingreso,
          // id del booking → prefiltra
          // /arriendos a solo este arriendo.
          b.id,
        ),
    },
    {
      key: "edit",
      label: "Editar",
      onClick: () => acciones.onEditBooking(b, propertyId),
    },
    { type: "divider" },
    {
      key: "copy-info",
      label: "Copiar información",
      onClick: () => acciones.onCopyInfo(b),
    },
    {
      key: "copy-inst",
      label: "Copiar instrucciones",
      onClick: () => acciones.onCopyInstructions(propertyId),
    },
    { type: "divider" },
    {
      // Eliminación cascada: reserva + aseo
      // AUTO + bloqueo salida tardía + cargos
      // adicionales. Ver
      // docs/calendario-cascada-arriendos.md §2.
      key: "delete-full",
      label: "Eliminar arriendo y aseo",
      danger: true,
      onClick: () =>
        acciones.onDeleteBooking(b.id, {
          preserveCleaning: false,
        }),
    },
    {
      // Eliminar solo la reserva (desvincula
      // el aseo AUTO con reserva_id=null para
      // que quede como evento independiente).
      key: "delete-only",
      label: "Eliminar solo arriendo (conservar aseo)",
      onClick: () =>
        acciones.onDeleteBooking(b.id, {
          preserveCleaning: true,
        }),
    },
  ];
}

/** Ítems del menú (⋮) de la barra de un evento. */
function itemsMenuEvento(
  ev: CalendarEventItem,
  acciones: AccionesBarra,
): MenuProps["items"] {
  return [
    {
      key: "edit",
      label: "Editar",
      onClick: () => acciones.onEditEvent(ev),
    },
    { type: "divider" },
    {
      key: "delete",
      label: "Eliminar",
      danger: true,
      onClick: () => acciones.onDeleteEvent(ev),
    },
  ];
}

/** Retraso del Tooltip de una barra al pasar el mouse, en segundos (el de siempre). */
const RETRASO_TOOLTIP_BARRA_S = 0.3;

/**
 * z-index del menú (⋮) sobre `zIndexPopupBase`. Cuando el Dropdown vivía dentro
 * del Tooltip de la barra, antd lo apilaba sobre el contexto del Tooltip
 * (contenedor: base + 100) con el desplazamiento de un Dropdown (+50): 1.150
 * con el token por defecto, por encima de la barra lateral (1.050 y 1.100). El
 * Dropdown a pedido ya no está dentro de un Tooltip, así que el valor se fija a
 * mano; calendarioGrid.popups.test.tsx lo compara con el que calcula antd para
 * la estructura anterior.
 */
const Z_MENU_BARRA_SOBRE_BASE = 150;

/**
 * Eventos que el Trigger de antd (Tooltip o Dropdown) le pone a su hijo para
 * abrir y cerrar el popup. Con hover son los de mouse y de puntero; en un
 * teléfono antd los cambia por `onClick`.
 */
type EventosDisparador = {
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  onPointerEnter?: (e: React.PointerEvent) => void;
  onPointerLeave?: (e: React.PointerEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
};

type Caja<T> = { current: T };

type PuenteDisparadorProps = EventosDisparador & {
  /** Elemento que ya existe y que antd debe tratar como su disparador. */
  destino: Caja<HTMLElement | null>;
  /** Donde quedan los eventos del Trigger para que el elemento real los llame. */
  eventos: Caja<EventosDisparador | null>;
  /**
   * Se llama al montar, cuando antd ya entregó sus eventos. Con React.StrictMode
   * se llama dos veces: React desconecta y vuelve a conectar los efectos.
   */
  alRegistrar?: (eventos: EventosDisparador) => void;
  className?: string;
  "aria-describedby"?: string;
  ref?: React.Ref<HTMLElement>;
};

/**
 * Hijo del Tooltip o del Dropdown montado a pedido. No dibuja nada: le entrega a
 * antd el elemento que ya estaba en la barra (por ref, que antd usa para
 * alinear el popup, observar su tamaño y reconocer un clic afuera) y deja en
 * `eventos` los handlers que antd le pasa, para que ese elemento los llame.
 * Envolver la barra misma con el Tooltip al montarlo cambiaría su lugar en el
 * árbol y React la volvería a crear: un clic a medias se perdería y la clase
 * `cal-bar-flash` que pone el buscador por código desaparecería. También copia
 * al elemento la clase y el `aria-describedby` que antd le agrega a su hijo
 * (`ant-tooltip-open`, `ant-dropdown-open`), mientras está montado.
 */
function PuenteDisparador({
  destino,
  eventos,
  alRegistrar,
  className,
  "aria-describedby": descritoPor,
  ref,
  onMouseEnter,
  onMouseLeave,
  onPointerEnter,
  onPointerLeave,
  onClick,
}: PuenteDisparadorProps) {
  useImperativeHandle(ref, () => destino.current as HTMLElement, [destino]);
  useLayoutEffect(() => {
    eventos.current = {
      onMouseEnter,
      onMouseLeave,
      onPointerEnter,
      onPointerLeave,
      onClick,
    };
  });
  useLayoutEffect(() => {
    // Corre después del efecto de arriba, así que los eventos ya están.
    if (eventos.current) alRegistrar?.(eventos.current);
    return () => {
      eventos.current = null;
    };
    // Solo al montar: alRegistrar se toma de ese render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventos]);
  useLayoutEffect(() => {
    const el = destino.current;
    if (!el) return;
    const clases = (className ?? "").split(" ").filter(Boolean);
    el.classList.add(...clases);
    if (descritoPor) el.setAttribute("aria-describedby", descritoPor);
    return () => {
      el.classList.remove(...clases);
      if (descritoPor) el.removeAttribute("aria-describedby");
    };
  });
  return null;
}

type MenuBarraProps = {
  boton: Caja<HTMLElement | null>;
  eventos: Caja<EventosDisparador | null>;
  abierto: boolean;
  items: MenuProps["items"];
  onAbiertoChange: (abierto: boolean) => void;
  /** Se llama cuando el menú terminó de cerrarse (animación incluida). */
  onCerrado: () => void;
};

/**
 * El Dropdown del menú (⋮) de una barra, montado solo mientras se usa. Avisa con
 * onCerrado cuando terminó de cerrarse; la barra decide cuándo desmontarlo.
 */
function MenuBarra({
  boton,
  eventos,
  abierto,
  items,
  onAbiertoChange,
  onCerrado,
}: MenuBarraProps) {
  const { token } = theme.useToken();
  const abiertoRef = useRef(abierto);
  useLayoutEffect(() => {
    abiertoRef.current = abierto;
  });
  // antd no tipa `afterPopupVisibleChange` en el Dropdown, pero lo entrega tal
  // cual al Trigger, que lo llama al terminar la animación de abrir o cerrar.
  const alTerminarAnimacion = {
    afterPopupVisibleChange: (visible: boolean) => {
      if (!visible && !abiertoRef.current) onCerrado();
    },
  };
  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      open={abierto}
      onOpenChange={(vis) => onAbiertoChange(vis)}
      overlayStyle={{ zIndex: token.zIndexPopupBase + Z_MENU_BARRA_SOBRE_BASE }}
      menu={{
        onClick: (info) => {
          info.domEvent.stopPropagation();
          onAbiertoChange(false);
        },
        items,
      }}
      {...alTerminarAnimacion}
    >
      <PuenteDisparador destino={boton} eventos={eventos} />
    </Dropdown>
  );
}

/** Estado de una barra que no necesita re-render. */
type ControlBarra = {
  barra: Caja<HTMLDivElement | null>;
  boton: Caja<HTMLSpanElement | null>;
  eventosTooltip: Caja<EventosDisparador | null>;
  eventosMenu: Caja<EventosDisparador | null>;
  /** El puntero está sobre la barra (según sus propios enter/leave). */
  dentro: boolean;
  /** El Tooltip montado está cerrado y ya terminó de animar el cierre. */
  tooltipCerrado: boolean;
  /** Espera del retraso antes de montar el Tooltip. */
  espera: ReturnType<typeof setTimeout> | undefined;
  /** Se pidió montar el Tooltip y React todavía no lo dibujó. */
  montajePendiente: boolean;
  /**
   * Salida del puntero que llegó con el montaje pendiente. Va al Trigger al
   * montar y se conserva hasta que el puntero vuelve a entrar o empieza otro
   * montaje.
   */
  salidaPendiente: {
    evento: "onMouseLeave" | "onPointerLeave";
    e: React.MouseEvent | React.PointerEvent;
  } | null;
  /** El menú terminó de cerrarse con el puntero encima: se desmonta cuando sale. */
  menuDesmontajePendiente: boolean;
};

const crearControlBarra = (): ControlBarra => ({
  barra: { current: null },
  boton: { current: null },
  eventosTooltip: { current: null },
  eventosMenu: { current: null },
  dentro: false,
  tooltipCerrado: false,
  espera: undefined,
  montajePendiente: false,
  salidaPendiente: null,
  menuDesmontajePendiente: false,
});

type BarraCalendarioProps = {
  className: string;
  style: React.CSSProperties;
  /** Código de estadía AF en el DOM (`data-booking-id`), si la barra es de un arriendo. */
  bookingId?: string;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Logo o ícono y texto de la barra (lo que va antes del botón ⋮). */
  children: React.ReactNode;
  /** Contenido del Tooltip. Se llama solo con el Tooltip montado. */
  tooltip: () => React.ReactNode;
  /** Ítems del menú (⋮), o null si la barra no lleva menú. Se llama solo con el menú montado. */
  menuItems: (() => MenuProps["items"]) | null;
  menuAbierto: boolean;
  onMenuAbiertoChange: (abierto: boolean) => void;
};

/**
 * Una barra de arriendo o de evento. En reposo son solo sus `div`, su texto y
 * su botón (⋮), sin ningún componente de antd.
 *
 * Tooltip: con el puntero encima se espera el mismo retraso de siempre y recién
 * ahí se monta, ya abierto, así que pasar el mouse rápido sobre muchas barras no
 * monta ninguno. Desde ese momento los eventos de la barra van al Trigger de
 * antd (vía PuenteDisparador), que decide como antes: cierra al salir con su
 * retraso, sigue abierto si el puntero pasa al popup y vuelve a abrir si el
 * puntero regresa. Cuando termina de cerrarse con el puntero afuera, se
 * desmonta. En un teléfono (la misma detección de antd, rc-util/isMobile) antd
 * no abre el Tooltip con hover sino con un toque que lo alterna, y lo cierra un
 * toque afuera: el primer toque lo monta abierto y los siguientes van al Trigger.
 *
 * Menú: el botón (⋮) abre poniendo la clave de la barra en openMenuKey y el
 * Dropdown se monta abierto; con el menú montado, el botón le entrega el clic al
 * Trigger, igual que antes. Queda en el mismo lugar del árbol que antes (dentro
 * de la barra), así que los eventos del popup burbujean igual. Se desmonta al
 * terminar de cerrarse con el puntero afuera; si el puntero sigue sobre la barra
 * (o sobre el menú, que para React es parte de ella), queda montado y oculto
 * hasta que sale.
 */
function BarraCalendario({
  className,
  style,
  bookingId,
  onClick,
  children,
  tooltip,
  menuItems,
  menuAbierto,
  onMenuAbiertoChange,
}: BarraCalendarioProps) {
  const [tooltipMontado, setTooltipMontado] = useState(false);
  const [menuMontado, setMenuMontado] = useState(false);
  const controlRef = useRef<ControlBarra | null>(null);
  if (controlRef.current === null) controlRef.current = crearControlBarra();
  const control = controlRef.current;

  // El Dropdown nace con el menú abierto y queda montado hasta que termina de
  // cerrarse con el puntero afuera (ver onCerrado). Sin menú disponible no hay
  // nada que montar.
  if (menuItems && menuAbierto && !menuMontado) setMenuMontado(true);
  if (!menuItems && menuMontado) setMenuMontado(false);

  const montarTooltip = () => {
    control.tooltipCerrado = false;
    control.montajePendiente = true;
    // Una salida guardada es del montaje anterior, no de este (ver
    // entregarSalidaPendiente).
    control.salidaPendiente = null;
    setTooltipMontado(true);
  };

  const entrar = (
    e: React.MouseEvent | React.PointerEvent,
    evento: "onMouseEnter" | "onPointerEnter",
  ) => {
    control.dentro = true;
    // Una salida guardada vale solo mientras el puntero sigue afuera (ver
    // entregarSalidaPendiente).
    control.salidaPendiente = null;
    const eventos = control.eventosTooltip.current;
    if (eventos) {
      // Volvió mientras el Tooltip, ya cerrado, se desmonta: afterOpenChange
      // encoló setTooltipMontado(false) y React todavía no lo procesa. El
      // desmontaje le cancelaría al Trigger la apertura que programa esta
      // entrada y no aparecería nada hasta salir y volver a entrar. React aplica
      // los updates en orden, así que este true lo deja montado; si ya estaba
      // montado sin nada pendiente, no vuelve a renderizar.
      if (control.tooltipCerrado) setTooltipMontado(true);
      (eventos[evento] as ((ev: typeof e) => void) | undefined)?.(e);
      return;
    }
    // Volvió antes de que se montara: la salida guardada ya se borró arriba.
    if (control.montajePendiente) return;
    if (isMobile()) return;
    clearTimeout(control.espera);
    control.espera = setTimeout(() => {
      // Si la barra ya no está (se desmontó), no hay nada que abrir.
      if (control.barra.current) montarTooltip();
    }, RETRASO_TOOLTIP_BARRA_S * 1000);
  };

  const salir = (
    e: React.MouseEvent | React.PointerEvent,
    evento: "onMouseLeave" | "onPointerLeave",
  ) => {
    control.dentro = false;
    // El menú se cerró con el puntero encima y quedó montado (ver onCerrado):
    // ahora se desmonta. Va antes de mirar el Tooltip porque el menú se puede
    // abrir y cerrar sin que el Tooltip se haya montado.
    if (control.menuDesmontajePendiente) {
      control.menuDesmontajePendiente = false;
      setMenuMontado(false);
    }
    clearTimeout(control.espera);
    const eventos = control.eventosTooltip.current;
    if (!eventos) {
      // La espera ya se cumplió y el Tooltip está por montarse (abierto), pero
      // el puntero salió antes de que React lo dibujara. Sin el Trigger quedaría
      // abierto sin nadie encima; se guarda la salida y se le entrega apenas
      // monta, así abre y cierra con su retraso, igual que cuando estaba montado.
      if (control.montajePendiente) control.salidaPendiente = { evento, e };
      return;
    }
    (eventos[evento] as ((ev: typeof e) => void) | undefined)?.(e);
    if (control.tooltipCerrado) setTooltipMontado(false);
  };

  const entregarSalidaPendiente = (eventos: EventosDisparador) => {
    control.montajePendiente = false;
    const salida = control.salidaPendiente;
    if (!salida || control.dentro) {
      control.salidaPendiente = null;
      return;
    }
    // Se entrega sin borrarla. Con React.StrictMode (src/index.tsx), React
    // desconecta y vuelve a conectar los efectos del Tooltip recién montado: la
    // limpieza del Trigger borra el cierre que programó esta entrega y el
    // registro vuelve a llamar aquí, que tiene que entregarla otra vez. Solo vale
    // para este montaje: la borran entrar y el próximo montarTooltip. Si no, un
    // toque en teléfono que monta sin pasar por entrar la recibía y el Tooltip
    // se cerraba solo, porque el Trigger detecta el teléfono en un efecto y su
    // primer render, el que llega aquí, trae los eventos de escritorio.
    const { evento, e } = salida;
    (eventos[evento] as ((ev: typeof e) => void) | undefined)?.(e);
  };

  const clic = (e: React.MouseEvent<HTMLDivElement>) => {
    const eventos = control.eventosTooltip.current;
    if (eventos) eventos.onClick?.(e);
    else if (isMobile()) montarTooltip();
    onClick(e);
  };

  const cambiarMenu = (abierto: boolean) => {
    // Se volvió a abrir antes de que el puntero saliera: ya no hay que
    // desmontarlo al salir.
    if (abierto) control.menuDesmontajePendiente = false;
    onMenuAbiertoChange(abierto);
  };

  const clicBotonMenu = (e: React.MouseEvent<HTMLSpanElement>) => {
    const eventos = control.eventosMenu.current;
    if (eventos?.onClick) eventos.onClick(e);
    else cambiarMenu(true);
    e.stopPropagation();
  };

  return (
    <>
      <div
        ref={control.barra}
        {...(bookingId != null ? { "data-booking-id": bookingId } : {})}
        className={className}
        style={style}
        onClick={clic}
        onMouseEnter={(e) => entrar(e, "onMouseEnter")}
        onPointerEnter={(e) => entrar(e, "onPointerEnter")}
        onMouseLeave={(e) => salir(e, "onMouseLeave")}
        onPointerLeave={(e) => salir(e, "onPointerLeave")}
      >
        <div className="cal-bar-label">
          {children}
          {menuItems && (
            <span
              ref={control.boton}
              className="cal-bar-menu"
              onClick={clicBotonMenu}
            >
              <MoreOutlined />
            </span>
          )}
          {menuItems && menuMontado && (
            <MenuBarra
              boton={control.boton}
              eventos={control.eventosMenu}
              abierto={menuAbierto}
              items={menuItems()}
              onAbiertoChange={cambiarMenu}
              onCerrado={() => {
                // Con el puntero todavía encima, desmontarlo saca del documento el
                // nodo que está bajo el puntero y el navegador no manda mouseout
                // desde él: solo un mouseover desde un nodo sin fibra, que React
                // trata como una entrada desde fuera de la ventana. La barra no
                // recibiría nunca la salida y el tooltip quedaría abierto. Oculto
                // pero montado, la salida llega; se desmonta en salir.
                if (control.dentro) control.menuDesmontajePendiente = true;
                else setMenuMontado(false);
              }}
            />
          )}
        </div>
      </div>
      {/* Hermano de la barra y no hijo: como cuando la envolvía, los eventos
          del popup no burbujean hasta el onClick de la barra. */}
      {tooltipMontado && (
        <Tooltip
          defaultOpen
          title={tooltip()}
          placement="top"
          mouseEnterDelay={RETRASO_TOOLTIP_BARRA_S}
          overlayClassName="cal-bar-tooltip"
          onOpenChange={(abierto) => {
            if (!abierto) return;
            // El Trigger volvió a abrir: si salir o afterOpenChange ya habían
            // pedido desmontarlo, este true lo anula (React aplica los updates en
            // orden). Pasa con un toque en teléfono: el pointerleave del toque pide
            // desmontar y el click del mismo toque reabre; como el primero es
            // continuo y el segundo discreto, React los procesaba en una sola
            // pasada y el Tooltip quedaba desmontado.
            control.tooltipCerrado = false;
            setTooltipMontado(true);
          }}
          afterOpenChange={(abierto) => {
            if (abierto) return;
            control.tooltipCerrado = true;
            if (!control.dentro) setTooltipMontado(false);
          }}
        >
          <PuenteDisparador
            destino={control.barra}
            eventos={control.eventosTooltip}
            alRegistrar={entregarSalidaPendiente}
          />
        </Tooltip>
      )}
    </>
  );
}

type FilaBarrasProps = GeometriaBarras & {
  row: CalendarRow;
  dates: string[];
  getCell: (row: CalendarRow, date: string) => CalendarCellData;
  /** Handler ESTABLE de clic: el mismo wrapper por ref que usan las celdas. */
  onCellClick: CellHandler;
  acciones: AccionesBarra;
  /**
   * Si las barras muestran el menú (⋮). Hoy depende solo de que llegue
   * barMenuActions: los ítems se muestran siempre y cada uno llama a su acción
   * con `?.`. Es un booleano para que el objeto nuevo de cada render no
   * invalide la fila.
   */
  menuDisponible: boolean;
  /** Clave del menú abierto SOLO si una barra de esta fila la tiene; si no, null. */
  menuAbierto: string | null;
  /** Setter estable del estado del menú abierto (setOpenMenuKey). */
  setMenuAbierto: React.Dispatch<React.SetStateAction<string | null>>;
};

/** Las barras de arriendos y eventos de UN track (`.cal-bars-layer`). */
const FilaBarras = memo(function FilaBarras({
  row,
  dates,
  minDate,
  maxDate,
  dateIndexMap,
  cellW,
  totalWidth,
  getCell,
  onCellClick,
  acciones,
  menuDisponible,
  menuAbierto,
  setMenuAbierto,
}: FilaBarrasProps) {
  const bookingBars = (row.bookings ?? []).map((b: any) => {
    const ingreso: string | undefined = b.ingreso ?? undefined;
    const salida: string | undefined = b.salida ?? undefined;
    if (!ingreso || !salida) return null;

    if (salida < minDate) return null;
    if (ingreso > maxDate) return null;

    const startsBefore = ingreso < minDate;
    const endsAfter = salida > maxDate;

    let startX = 0;
    let endX = totalWidth;

    // reserva: ingreso 15:00 (evening), salida 11:00 (morning end)
    if (!startsBefore) {
      const idx = dateIndexMap.get(ingreso);
      if (idx === undefined) return null;
      startX = idx * cellW + THIRD.eveningStart * cellW;
    }
    if (!endsAfter) {
      const idx = dateIndexMap.get(salida);
      if (idx === undefined) return null;
      endX = idx * cellW + THIRD.morningEnd * cellW;
    }

    const w = Math.max(0, endX - startX);
    if (w < 2) return null;

    const platformKey = normalizePlatform(b.nombrePlataforma);
    const logoSrc = PLATFORM_LOGO[platformKey];

    const platformCode =
      b.idReservaPlataforma ?? b.idReservaStays ?? "";
    const total = Number(b.reservaTotalClp) ?? 0;
    const label = `${b.nombreHuesped ?? ""} ${
      platformCode ? `${platformCode}` : ""
    } $${total.toLocaleString("es-CL")}`.trim();

    const clave = claveMenuReserva(b);

    return (
      <BarraCalendario
        key={`bk-${b.id ?? platformCode ?? `${ingreso}-${salida}`}`}
        // Código de estadía AF en el DOM para que el buscador
        // por código del calendario (calendario.tsx) pueda
        // hacer scroll y resaltar esta barra concreta.
        bookingId={b.id != null ? String(b.id) : undefined}
        className={[
          "cal-booking-bar",
          startsBefore ? "flat-left" : "",
          endsAfter ? "flat-right" : "",
          w < 50 ? "narrow" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          left: startX,
          width: w,
          // Calendario siempre usa el celeste base, NO el
          // `property.color` que tiene cada propiedad. Ese
          // campo se introdujo (commit bca6f5a) para los
          // chips de /tareas — el calendario debe seguir
          // viéndose uniforme como antes.
          backgroundColor: "#2EC5F5",
        }}
        onClick={(e) => {
          const date = dateFromClickX(e, dates, cellW);
          const third = thirdFromClickX(e, dates, cellW);
          const cell = getCell(row, date);
          onCellClick(cell, third, e);
        }}
        tooltip={() => contenidoTooltipReserva(b, ingreso, salida)}
        menuItems={
          menuDisponible
            ? () => itemsMenuReserva(b, row.propertyId, ingreso, acciones)
            : null
        }
        menuAbierto={menuAbierto === clave}
        onMenuAbiertoChange={(abierto) =>
          setMenuAbierto(abierto ? clave : null)
        }
      >
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={platformKey}
            className="cal-bar-logo"
          />
        ) : null}
        <span className="cal-bar-text">{label}</span>
      </BarraCalendario>
    );
  });

  const geo: GeometriaBarras = { minDate, maxDate, dateIndexMap, cellW, totalWidth };

  const eventBars = (row.events ?? []).map(
    (ev: CalendarEventItem) => {
      const start = new Date(ev.startISO);
      const end = new Date(ev.endISO);
      const pos = calcBarPosition(start, end, geo);
      if (!pos) return null;

      const icon =
        ev.kind === "blocked"
          ? "🔒"
          : ev.kind === "maintenance"
            ? "🛠️"
            : "🧹";
      const baseTitle =
        ev.kind === "blocked"
          ? "Bloqueo"
          : ev.kind === "maintenance"
            ? "Mantención"
            : "Aseo";

      const extra =
        ev.kind === "cleaning"
          ? (ev.notas ?? "")
          : (ev.razon ?? "");
      const label = `${baseTitle}${extra ? `: ${extra}` : ""}`;

      const clave = claveMenuEvento(ev);

      return (
        <BarraCalendario
          key={clave}
          className={[
            "cal-event-bar",
            ev.kind,
            // Bloqueo o mantención creada por el cliente
            // (no admin): se pinta violeta para coincidir
            // con la vista del propietario y diferenciarla
            // de los bloqueos/mantenciones del propio admin.
            (ev.kind === "blocked" ||
              ev.kind === "maintenance") &&
            (ev as any).createdByAdmin === false
              ? "is-from-client"
              : "",
            pos.flatLeft ? "flat-left" : "",
            pos.flatRight ? "flat-right" : "",
            pos.width < 50 ? "narrow" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ left: pos.startX, width: pos.width }}
          onClick={(e) => {
            const date = dateFromClickX(e, dates, cellW);
            const third = thirdFromClickX(e, dates, cellW);
            const cell = getCell(row, date);
            onCellClick(cell, third, e);
          }}
          tooltip={() => contenidoTooltipEvento(ev)}
          menuItems={
            menuDisponible && pos.width >= 50
              ? () => itemsMenuEvento(ev, acciones)
              : null
          }
          menuAbierto={menuAbierto === clave}
          onMenuAbiertoChange={(abierto) =>
            setMenuAbierto(abierto ? clave : null)
          }
        >
          <span className="cal-bar-icon">{icon}</span>
          <span className="cal-bar-text">{label}</span>
        </BarraCalendario>
      );
    },
  );

  return (
    <div className="cal-bars-layer">
      <div className="cal-bars-track">
        {bookingBars}
        {eventBars}
      </div>
    </div>
  );
});

type MetaDraft = {
  field: MetaField;
  propertyId: number;
  startDate: string;
  endDate?: string;
};

/** Rango destacado de una fila meta: solo la fila (campo + propiedad) en draft. */
function rangoDestacado(
  draft: MetaDraft | null,
  propertyId: number,
  field: MetaField,
): [string, string] | null {
  return draft && draft.field === field && draft.propertyId === propertyId
    ? [draft.startDate, draft.endDate ?? draft.startDate]
    : null;
}

type FilasMetaPropiedadProps = {
  propertyId: number;
  staysData: StaysCalendarMeta;
  dates: string[];
  /** Las mismas columnas que la cabecera de fechas (ver CalendarioMetaRow). */
  gridTemplateColumns: string;
  canEditMeta: boolean;
  refreshing: boolean | undefined;
  /** Draft de edición por rango SOLO si es de esta propiedad; si no, null. */
  draft: MetaDraft | null;
  /** Handler ESTABLE (wrapper por ref de handleMetaCellClick). */
  onMetaCellClick: (field: MetaField, propertyId: number, date: string) => void;
};

/**
 * Las 5 filas meta de una propiedad expandida. Memoizada: con 427 días cada
 * fila son 427 celdas y las de precio formatean CLP en cada una.
 */
const FilasMetaPropiedad = memo(function FilasMetaPropiedad({
  propertyId,
  staysData,
  dates,
  gridTemplateColumns,
  canEditMeta,
  refreshing,
  draft,
  onMetaCellClick,
}: FilasMetaPropiedadProps) {
  const clickBase = useCallback(
    (date: string) => onMetaCellClick("base", propertyId, date),
    [onMetaCellClick, propertyId],
  );
  const clickCondicion = useCallback(
    (date: string) => onMetaCellClick("condicion", propertyId, date),
    [onMetaCellClick, propertyId],
  );
  const clickMinNoches = useCallback(
    (date: string) => onMetaCellClick("minNoches", propertyId, date),
    [onMetaCellClick, propertyId],
  );
  const clickRestriccion = useCallback(
    (date: string) => onMetaCellClick("restriccion", propertyId, date),
    [onMetaCellClick, propertyId],
  );
  // La tupla se memoiza para que CalendarioMetaRow (memoizada) no reciba un
  // array nuevo en cada render.
  const destacadoBase = useMemo(
    () => rangoDestacado(draft, propertyId, "base"),
    [draft, propertyId],
  );
  const destacadoCondicion = useMemo(
    () => rangoDestacado(draft, propertyId, "condicion"),
    [draft, propertyId],
  );
  const destacadoMinNoches = useMemo(
    () => rangoDestacado(draft, propertyId, "minNoches"),
    [draft, propertyId],
  );
  const destacadoRestriccion = useMemo(
    () => rangoDestacado(draft, propertyId, "restriccion"),
    [draft, propertyId],
  );

  return (
    <>
      {/* `refreshing`: tras aplicar un cambio, las 5 filas meta de
          esta propiedad pulsan con spinner hasta que llega la
          re-lectura con los valores definitivos. */}
      <CalendarioMetaRow
        label="Precio base ($)"
        tone="base"
        info={
          canEditMeta
            ? "Precio base por noche (clic para editar por rango). A Stays va el efectivo = base + condición."
            : "Precio base por noche. A Stays va el efectivo = base + condición."
        }
        dates={dates}
        gridTemplateColumns={gridTemplateColumns}
        values={staysData.base}
        format={formatClp}
        clickable={canEditMeta}
        onCellClick={clickBase}
        highlightRange={destacadoBase}
        refreshing={refreshing}
      />
      <CalendarioMetaRow
        label="Condición"
        tone="condicion"
        info="Ajuste sobre el base (promoción − / adición +), en % o monto. Vive solo en mialoja; a Stays va el efectivo."
        dates={dates}
        gridTemplateColumns={gridTemplateColumns}
        values={staysData.condicion}
        clickable={canEditMeta}
        onCellClick={clickCondicion}
        highlightRange={destacadoCondicion}
        refreshing={refreshing}
      />
      <CalendarioMetaRow
        label="Precio efectivo ($)"
        tone="efectivo"
        info="Lo que se vende/envía a Stays = base + condición. Solo lectura."
        dates={dates}
        gridTemplateColumns={gridTemplateColumns}
        values={staysData.efectivo ?? staysData.base}
        format={formatClp}
        refreshing={refreshing}
      />
      <CalendarioMetaRow
        label="Min. noches"
        tone="minnoches"
        info="Mínimo de noches para reservar. Se sincroniza con Stays (ida y vuelta)."
        dates={dates}
        gridTemplateColumns={gridTemplateColumns}
        values={staysData.minNoches}
        clickable={canEditMeta}
        onCellClick={clickMinNoches}
        highlightRange={destacadoMinNoches}
        refreshing={refreshing}
      />
      <CalendarioMetaRow
        label="Restricciones"
        tone="restriccion"
        info="IN = cerrado a llegada · OUT = cerrado a salida · IN OUT = ambas. Se sincroniza con Stays."
        dates={dates}
        gridTemplateColumns={gridTemplateColumns}
        values={staysData.restricciones}
        clickable={canEditMeta}
        onCellClick={clickRestriccion}
        highlightRange={destacadoRestriccion}
        refreshing={refreshing}
      />
    </>
  );
});

type FilaMercadoProps = {
  /** Nombre ya formateado (MAYÚSCULAS) de la propiedad. */
  nombre: string;
  datos: MercadoCalendarioResumenItem | undefined;
  dates: string[];
  gridTemplateColumns: string;
  paleta: MercadoPaleta;
  staysData: StaysCalendarMeta | undefined;
};

/**
 * Fila informativa de MERCADO de una propiedad. Memoizada: partir el rango en
 * segmentos recorre todos los días contra todos los combos, y cada segmento
 * lleva su Tooltip. Solo se recalcula cuando cambian sus datos, la paleta, el
 * rango o la meta (la flecha compara con el precio base).
 */
const FilaMercado = memo(function FilaMercado({
  nombre,
  datos: m,
  dates,
  gridTemplateColumns,
  paleta: mercadoPaleta,
  staysData,
}: FilaMercadoProps) {
  const segmentos = m?.combos ? buildMercadoSegments(m.combos, dates) : [];
  // Sin datos (sin código de mercado o sin combos que caigan en
  // el rango visible).
  if (!m || !m.tieneDatos || !segmentos.length) {
    return (
      <Fragment>
        <div className="cal-prop cal-sticky-col cal-mercado-label cal-mercado-label--empty">
          <div className="cal-mercado-label-top">
            <span className="cal-mercado-ico" aria-hidden>
              📊
            </span>
            <span className="cal-mercado-label-text">Mercado · {nombre}</span>
          </div>
        </div>
        <div className="cal-row-dates cal-row-dates--mercado">
          <div className="cal-dates-grid" style={{ gridTemplateColumns }}>
            <div className="cal-mercado-empty" style={{ gridColumn: "1 / -1" }}>
              <span className="cal-mercado-empty-text">Sin datos de mercado</span>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="cal-prop cal-sticky-col cal-mercado-label">
        <div className="cal-mercado-label-top">
          <span className="cal-mercado-ico" aria-hidden>
            📊
          </span>
          <span className="cal-mercado-label-text">Mercado · {nombre}</span>
        </div>
      </div>
      <div className="cal-row-dates cal-row-dates--mercado">
        <div className="cal-dates-grid" style={{ gridTemplateColumns }}>
          {segmentos.map((seg) => {
            const c = seg.combo;
            const ocup = c.ocupacion;
            const color = colorDeOcupacion(mercadoPaleta, ocup);
            const fg = textoContraste(color);
            const nivel = nivelDeOcupacion(ocup);
            const bandaTxt = c.banda ? fmtBandaMiles(c.banda) : null;

            // Flecha: compara el base actual (si la meta ya está
            // cargada al expandir) con la banda de ESTE tramo.
            const segDates = dates.slice(seg.startIdx, seg.endIdx + 1);
            const base = baseRepDelRango(staysData, segDates);
            let flecha: React.ReactNode = null;
            let flechaTip = "";
            if (base != null && c.banda) {
              if (base < c.banda.desde) {
                flecha = <ArrowUpOutlined className="cal-mercado-flecha" />;
                flechaTip = `Tu base (${base.toLocaleString("es-CL")}) está bajo la banda — podrías subir.`;
              } else if (base > c.banda.hasta) {
                flecha = <ArrowDownOutlined className="cal-mercado-flecha" />;
                flechaTip = `Tu base (${base.toLocaleString("es-CL")}) está sobre la banda.`;
              } else {
                flecha = <CheckOutlined className="cal-mercado-flecha" />;
                flechaTip = `Tu base (${base.toLocaleString("es-CL")}) está dentro de la banda.`;
              }
            }

            const tip =
              `Estadía ${c.checkin} → ${c.checkout} (${c.noches} ${c.noches === 1 ? "noche" : "noches"}) · ` +
              `${nivelLabel(nivel)} · ocupación ${ocup}%` +
              (c.banda
                ? ` · precio sugerido ${c.banda.desde.toLocaleString("es-CL")}–${c.banda.hasta.toLocaleString("es-CL")}`
                : "") +
              (flechaTip ? ` · ${flechaTip}` : "");

            return (
              <Tooltip
                key={`${c.checkin}|${c.checkout}|${seg.startIdx}`}
                title={tip}
                mouseEnterDelay={0.3}
              >
                <div
                  className="cal-mercado-seg"
                  style={{ gridColumn: `${seg.startIdx + 1} / ${seg.endIdx + 2}` }}
                >
                  {/* Relleno de abajo→arriba a la ocupación del
                      combo; color de la paleta. */}
                  <div
                    className="cal-mercado-fill"
                    style={{ height: `${ocup}%`, background: color }}
                  />
                  {/* Etiqueta centrada sobre el tramo por defecto y
                      STICKY: sigue el scroll horizontal para
                      mantenerse visible. Contraste WCAG (fg). */}
                  <div className="cal-mercado-seg-label" style={{ color: fg }}>
                    <span className="cal-mercado-ocup">{ocup}%</span>
                    {bandaTxt && (
                      <span className="cal-mercado-precio">· {bandaTxt}</span>
                    )}
                    {flecha && (
                      <span className="cal-mercado-flecha-wrap">{flecha}</span>
                    )}
                  </div>
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </Fragment>
  );
});

export default function CalendarioGrid({
  calendarRows,
  dates,
  getCell,
  todayISO,
  onCellClick,
  onCellDoubleClick,
  onCellContextMenu,
  rangeSelection,
  barMenuActions,
  wrapperRef,
  onWrapperScroll,
  onMetaRangeComplete,
  staysCalendarData: staysCalendarDataProp,
  fetchPropertyMeta,
  canEditMeta = false,
  propertyInfoById,
  metaRefreshingIds,
  mercadoEnabled = false,
  mercadoPaleta,
  mercadoByProperty,
}: Props) {
  const dateToIdx = useMemo(() => {
    const map = new Map<string, number>();
    dates.forEach((d, i) => map.set(d, i));
    return map;
  }, [dates]);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const isMobile = !screens.md;
  const cellW = isMobile ? 56 : 72;
  const propW = isMobile ? 160 : 260;

  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  // ── Crosshair de hover (guía sutil fila + columna) ──
  // La FILA se resalta 100% por CSS (:hover, ver calendario.css). La COLUMNA
  // se mueve acá: un único <div className="cal-crosshair-col"> posicionado por
  // transform:translateX, actualizado con rAF + guard por índice. Es O(1) por
  // frame y NO usa setState (mutación directa de estilo → cero re-renders del
  // grid). Ver docs/modulo-calendario.md.
  const crosshairColRef = useRef<HTMLDivElement>(null);
  const rafPendingRef = useRef(false);
  const lastColRef = useRef(-1);
  const lastMoveXRef = useRef(0);

  const hideCrosshair = useCallback(() => {
    const el = crosshairColRef.current;
    if (el) el.style.opacity = "0";
    lastColRef.current = -1;
  }, []);

  const onCrosshairMove = useCallback(
    (e: React.MouseEvent) => {
      lastMoveXRef.current = e.clientX;
      if (rafPendingRef.current) return;
      rafPendingRef.current = true;
      requestAnimationFrame(() => {
        rafPendingRef.current = false;
        const el = crosshairColRef.current;
        const grid = el?.parentElement; // .cal-grid-2col (ancla position:relative)
        if (!el || !grid) return;
        const gridRect = grid.getBoundingClientRect();
        // relX = X dentro de la zona de fechas (descontada la columna
        // propiedad). gridRect.left ya refleja el scroll horizontal, así que
        // esto funciona scrolleado sin sumar scrollLeft. La barra se ancla al
        // mismo sistema de coords del grid, por eso translateX no usa scroll.
        const relX = lastMoveXRef.current - gridRect.left - propW;
        if (relX < 0) {
          // cursor sobre la columna propiedad sticky → sin columna que marcar
          hideCrosshair();
          return;
        }
        const idx = Math.min(Math.floor(relX / cellW), dates.length - 1);
        if (idx < 0) {
          hideCrosshair();
          return;
        }
        if (idx === lastColRef.current) {
          el.style.opacity = "1";
          return; // misma columna → no tocar el DOM
        }
        lastColRef.current = idx;
        el.style.transform = `translateX(${propW + idx * cellW}px)`;
        el.style.opacity = "1";
      });
    },
    [cellW, propW, dates.length, hideCrosshair],
  );

  useEffect(() => {
    const el = wrapperRef?.current;
    if (!el) return;
    // Cerramos menús abiertos y ocultamos el crosshair en cada scroll: la
    // banda quedaría desfasada durante el scroll (se re-muestra al primer
    // mousemove). hideCrosshair es estable (useCallback []).
    const onScroll = () => {
      setOpenMenuKey(null);
      hideCrosshair();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [wrapperRef, hideCrosshair]);

  const rangeMeta = useMemo<RangeMeta | null>(() => {
    if (!rangeSelection) return null;

    const a = dateToIdx.get(rangeSelection.startDate);
    const b = dateToIdx.get(rangeSelection.endDate);
    if (a === undefined || b === undefined) return null;

    const startIdx = Math.min(a, b);
    const endIdx = Math.max(a, b);
    const forward = a <= b;

    const rawStartThird = forward
      ? rangeSelection.startThird
      : rangeSelection.endThird;
    const rawEndThird = forward
      ? rangeSelection.endThird
      : rangeSelection.startThird;

    if (!rawStartThird || !rawEndThird) return null;

    return {
      propertyId: rangeSelection.propertyId,
      startIdx,
      endIdx,
      startThird: rawStartThird,
      endThird: rawEndThird,
    };
  }, [rangeSelection, dateToIdx]);

  const [expandedProperties, setExpandedProperties] = useState<
    Record<number, boolean>
  >({});
  // Draft de selección por rango para CUALQUIER fila meta editable (base /
  // condición / min noches / restricción). Se ancla al (campo, propiedad): el
  // primer click fija el inicio en esa fila; el segundo completa el rango y
  // abre el modal de edición. Antes esto sólo existía para "Base ($)".
  const [metaDraft, setMetaDraft] = useState<{
    field: MetaField;
    propertyId: number;
    startDate: string;
    endDate?: string;
  } | null>(null);

  const handleMetaCellClick = useCallback(
    (field: MetaField, propertyId: number, date: string) => {
      if (!metaDraft || metaDraft.field !== field || metaDraft.propertyId !== propertyId) {
        setMetaDraft({ field, propertyId, startDate: date });
        return;
      }
      // Segundo click — completar rango
      const start = metaDraft.startDate <= date ? metaDraft.startDate : date;
      const end = metaDraft.startDate <= date ? date : metaDraft.startDate;
      setMetaDraft(null);
      onMetaRangeComplete?.(field, propertyId, start, end);
    },
    [metaDraft, onMetaRangeComplete],
  );

  // ── Handlers ESTABLES para las filas memoizadas ──
  // calendario.tsx pasa onCellClick/onCellDoubleClick/onCellContextMenu (y la
  // grilla arma handleMetaCellClick) como funciones nuevas en cada render,
  // porque leen mucho estado. Pasarlas tal cual invalidaría todas las filas
  // memoizadas. Guardamos la ÚLTIMA versión en una ref y entregamos wrappers
  // con identidad fija que llaman a la ref: un clic siempre ejecuta la versión
  // del último render confirmado, nunca una closure vieja. La ref se actualiza
  // en un layout effect (no durante el render, que en modo concurrente puede
  // descartarse); corre en el commit, antes de que llegue cualquier evento.
  const onCellClickRef = useRef(onCellClick);
  const onCellDoubleClickRef = useRef(onCellDoubleClick);
  const onCellContextMenuRef = useRef(onCellContextMenu);
  const handleMetaCellClickRef = useRef(handleMetaCellClick);
  const barMenuActionsRef = useRef(barMenuActions);
  useLayoutEffect(() => {
    onCellClickRef.current = onCellClick;
    onCellDoubleClickRef.current = onCellDoubleClick;
    onCellContextMenuRef.current = onCellContextMenu;
    handleMetaCellClickRef.current = handleMetaCellClick;
    barMenuActionsRef.current = barMenuActions;
  });
  const cellClickEstable = useCallback<CellHandler>(
    (c, third, e) => onCellClickRef.current?.(c, third, e),
    [],
  );
  const cellDoubleClickEstable = useCallback<CellHandler>(
    (c, third, e) => onCellDoubleClickRef.current?.(c, third, e),
    [],
  );
  const cellContextMenuEstable = useCallback(
    () => onCellContextMenuRef.current?.(),
    [],
  );
  const metaCellClickEstable = useCallback(
    (field: MetaField, propertyId: number, date: string) =>
      handleMetaCellClickRef.current(field, propertyId, date),
    [],
  );
  // Acciones del menú (⋮) de las barras: mismo patrón. Cada invocador lee la
  // acción de la ref AL EJECUTARSE, con `?.` como antes, así que si la versión
  // actual no trae una acción el ítem no hace nada (no cae en una anterior).
  const accionesBarraEstables = useMemo<AccionesBarra>(
    () => ({
      onViewFinanzas: (propertyId, date, bookingId) =>
        barMenuActionsRef.current?.onViewFinanzas?.(propertyId, date, bookingId),
      onEditBooking: (booking, propertyId) =>
        barMenuActionsRef.current?.onEditBooking?.(booking, propertyId),
      onEditEvent: (event) => barMenuActionsRef.current?.onEditEvent?.(event),
      onDeleteBooking: (bookingId, opts) =>
        barMenuActionsRef.current?.onDeleteBooking?.(bookingId, opts),
      onDeleteEvent: (event) =>
        barMenuActionsRef.current?.onDeleteEvent?.(event),
      onCopyInfo: (booking) => barMenuActionsRef.current?.onCopyInfo?.(booking),
      onCopyInstructions: (propertyId) =>
        barMenuActionsRef.current?.onCopyInstructions?.(propertyId),
    }),
    [],
  );
  // Disponibilidad: lo único que hoy depende de que existan las acciones es el
  // menú entero (`barMenuActions && <Dropdown…>`). Los ítems no se condicionan
  // uno a uno, así que no hace falta un booleano por acción.
  const menuDisponible = !!barMenuActions;

  // Filas con alguna barra cuya clave coincide con el menú abierto. Solo esas
  // reciben openMenuKey; las demás reciben null y no se re-renderizan al abrir o
  // cerrar un menú. Se busca por clave (no por la fila donde se hizo clic) para
  // que el resultado sea idéntico a comparar openMenuKey en cada barra, también
  // si un refresco mueve la barra de track con el menú abierto.
  const filasConMenuAbierto = useMemo(() => {
    if (openMenuKey == null) return null;
    const filas = new Set<CalendarRow>();
    for (const row of calendarRows) {
      if (
        (row.bookings ?? []).some((b: any) => claveMenuReserva(b) === openMenuKey) ||
        (row.events ?? []).some((ev) => claveMenuEvento(ev) === openMenuKey)
      ) {
        filas.add(row);
      }
    }
    return filas;
  }, [openMenuKey, calendarRows]);

  const datesGridTemplateColumns = useMemo(
    () => `repeat(${dates.length}, ${cellW}px)`,
    [dates.length, cellW],
  );

  const rowsByProperty = useMemo(() => {
    const map = new Map<number, CalendarRow[]>();
    calendarRows.forEach((row) => {
      if (!map.has(row.propertyId)) map.set(row.propertyId, []);
      map.get(row.propertyId)!.push(row);
    });
    return Array.from(map.entries());
  }, [calendarRows]);

  const toggleProperty = (propertyId: number) => {
    setExpandedProperties((prev) => {
      const wasExpanded = !!prev[propertyId];
      if (!wasExpanded && fetchPropertyMeta) {
        fetchPropertyMeta(propertyId);
      }
      return { ...prev, [propertyId]: !wasExpanded };
    });
  };

  const minDate = dates[0];
  const maxDate = dates[dates.length - 1];
  const totalWidth = dates.length * cellW;

  // Mapa fecha → índice para posicionar las barras. dateFromClickX,
  // thirdFromClickX y calcBarPosition viven a nivel de módulo (reciben la
  // geometría por parámetro) para no crear funciones nuevas en cada render.
  const dateIndexMap = useMemo(() => {
    const m = new Map<string, number>();
    dates.forEach((d, i) => m.set(d, i));
    return m;
  }, [dates]);

  // Bandas de fin de semana y cabecera de fechas memoizadas (round 2026-09-11):
  // no dependen de la selección ni de los datos. formatHeader llama dos veces a
  // toLocaleDateString por día —con 427 días son ~58 ms medidos en Node 24— y
  // sin memo ese costo se pagaba en cada clic de selección y en cada re-render
  // de calendario.tsx.
  const bandasFinDeSemana = useMemo(
    () =>
      dates.map((date, i) => {
        const dow = new Date(`${date}T00:00:00`).getDay();
        if (dow !== 5 && dow !== 6) return null;
        return (
          <div
            key={`wk-${date}`}
            className="cal-weekend-col"
            style={{ transform: `translateX(${propW + i * cellW}px)` }}
            aria-hidden
          />
        );
      }),
    [dates, propW, cellW],
  );

  // Columna de hoy (round 2026-09-15): una sola banda, como las de fin de
  // semana, que cubre toda la altura de la grilla (filas, filas meta y Mercado).
  // Así hoy se encuentra de un vistazo aunque sus celdas estén ocupadas. Si hoy
  // no está en el rango, no se dibuja.
  const indiceHoy = dateIndexMap.get(todayISO);
  const bandaHoy =
    indiceHoy === undefined ? null : (
      <div
        className="cal-today-col"
        style={{ transform: `translateX(${propW + indiceHoy * cellW}px)` }}
        aria-hidden
      />
    );

  const cabeceraFechas = useMemo(
    () =>
      dates.map((date, i) => {
        const { day, weekday, month, showMonth } = formatHeader(
          date,
          i === 0,
        );
        const isToday = date === todayISO;
        const dow = new Date(`${date}T00:00:00`).getDay(); // 5=vie, 6=sáb
        const isWeekend = dow === 5 || dow === 6;
        return (
          <div
            key={date}
            className={[
              "cal-head",
              isToday ? "is-today" : "",
              isWeekend ? "is-weekend" : "",
            ].join(" ")}
            aria-current={isToday ? "date" : undefined}
          >
            {/* Hoy dice «Hoy» en la línea del mes: las columnas vecinas ya
                muestran el mes, y así la cabecera no crece. */}
            {isToday ? (
              <div className="cal-head-hoy">Hoy</div>
            ) : (
              showMonth && <div className="cal-head-month">{month}</div>
            )}
            <div className="cal-head-day">{day}</div>
            <div className="cal-head-weekday">{weekday}</div>
          </div>
        );
      }),
    [dates, todayISO],
  );

  return (
    <div
      // .is-movil sale del mismo isMobile que fija cellW: las reglas de
      // calendario.css que achican el texto de las filas meta se aplican
      // justo cuando la columna mide 56 px.
      className={isMobile ? "cal-wrapper is-movil" : "cal-wrapper"}
      ref={wrapperRef}
      onScroll={onWrapperScroll}
      onMouseMove={onCrosshairMove}
      onMouseLeave={hideCrosshair}
      style={
        {
          ["--cell-w" as any]: `${cellW}px`,
          ["--prop-w" as any]: `${propW}px`,
        } as React.CSSProperties
      }
    >
      <div className="cal-grid-2col">
        {/* Banda de columna del crosshair (guía de hover). position:absolute,
            movida por transform desde onCrosshairMove. pointer-events:none →
            no interfiere con clicks. Oculta en táctil (CSS). */}
        <div className="cal-crosshair-col" ref={crosshairColRef} aria-hidden />
        {/* Bandas tenues de fin de semana (vie/sáb): días "premium", suelen ser
            más caros. Overlay NO interactivo (pointer-events:none), una banda por
            columna, posicionada igual que el crosshair (translateX). */}
        {bandasFinDeSemana}
        {/* Columna de hoy: rayado violeta sobre toda la altura, no interactiva. */}
        {bandaHoy}
        {/* ===== Header ===== */}
        <div className="cal-head cal-sticky-row cal-sticky-col">Propiedad</div>

        <div className="cal-row-dates cal-sticky-row">
          <div
            className="cal-dates-grid"
            style={{ gridTemplateColumns: datesGridTemplateColumns }}
          >
            {cabeceraFechas}
          </div>
        </div>

        {/* ===== Rows ===== */}
        {rowsByProperty.map(([propertyId, rows]) => {
          const isExpanded = !!expandedProperties[propertyId];
          const staysData = staysCalendarDataProp?.[propertyId];

          return (
            <Fragment key={propertyId}>
              {rows.map((row) => {
                return (
                  <Fragment key={`${row.propertyId}-${row.trackIndex}`}>
                    <div
                      className={`cal-prop cal-sticky-col${row.trackIndex > 0 ? " cal-subrow" : ""}`}
                      {...(row.trackIndex === 0 ? { "data-property-id": String(row.propertyId) } : {})}
                    >
                      {row.trackIndex === 0 && (
                        <button
                          className="cal-expand-btn"
                          onClick={() => toggleProperty(propertyId)}
                          type="button"
                        >
                          {isExpanded ? (
                            <CaretDownOutlined />
                          ) : (
                            <CaretRightOutlined />
                          )}
                        </button>
                      )}
                      {row.trackIndex === 0
                        ? (() => {
                            const info = propertyInfoById?.[propertyId];
                            // Subtexto en MINI-FILAS: geo (región + sector) en una,
                            // cliente en otra. Cada dato se lee sin apretarse.
                            const geoChips: Array<{ key: string; cls: string; text: string }> = [];
                            if (info?.regionNombre)
                              geoChips.push({
                                key: "region",
                                cls: "cal-chip-region",
                                text: info.regionNombre,
                              });
                            if (info?.sectorNombre)
                              geoChips.push({
                                key: "sector",
                                cls: "cal-chip-sector",
                                text: info.sectorNombre,
                              });
                            const clienteNombre = info?.clienteNombre ?? null;
                            const subTooltip = [
                              info?.regionNombre && `Región: ${info.regionNombre}`,
                              info?.comunaNombre && `Comuna: ${info.comunaNombre}`,
                              info?.sectorNombre && `Sector: ${info.sectorNombre}`,
                              info?.clienteNombre && `Cliente: ${info.clienteNombre}`,
                            ]
                              .filter(Boolean)
                              .join(" · ");
                            const hasSub = geoChips.length > 0 || !!clienteNombre;
                            return (
                              <div className="cal-prop-info">
                                <Tooltip
                                  title={formatPropertyName(row.propertyName)}
                                  mouseEnterDelay={1}
                                >
                                  <span
                                    className="cal-prop-main"
                                    onClick={() => toggleProperty(propertyId)}
                                  >
                                    {formatPropertyName(row.propertyName)}
                                  </span>
                                </Tooltip>
                                {hasSub && (
                                  <Tooltip title={subTooltip} mouseEnterDelay={0.4}>
                                    <div className="cal-prop-sub">
                                      {geoChips.length > 0 && (
                                        <div className="cal-prop-subrow">
                                          {geoChips.map((c) => (
                                            <span
                                              key={c.key}
                                              className={`cal-chip ${c.cls}`}
                                            >
                                              {c.text}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                      {clienteNombre && (
                                        <div className="cal-prop-subrow">
                                          <span className="cal-chip cal-chip-cliente">
                                            {clienteNombre}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </Tooltip>
                                )}
                              </div>
                            );
                          })()
                        : ""}
                    </div>

                    <div className="cal-row-dates">
                      {/* Barras del track: memoizadas por fila (ver
                          FilaBarras). Solo las filas con una barra cuyo menú
                          está abierto reciben openMenuKey. */}
                      <FilaBarras
                        row={row}
                        dates={dates}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateIndexMap={dateIndexMap}
                        cellW={cellW}
                        totalWidth={totalWidth}
                        getCell={getCell}
                        onCellClick={cellClickEstable}
                        acciones={accionesBarraEstables}
                        menuDisponible={menuDisponible}
                        menuAbierto={
                          filasConMenuAbierto?.has(row) ? openMenuKey : null
                        }
                        setMenuAbierto={setOpenMenuKey}
                      />

                      {/* Celdas del track: memoizadas por fila (ver
                          FilaCeldas). Solo la propiedad seleccionada recibe
                          la selección; las demás reciben null. */}
                      <FilaCeldas
                        row={row}
                        dates={dates}
                        gridTemplateColumns={datesGridTemplateColumns}
                        todayISO={todayISO}
                        getCell={getCell}
                        seleccion={
                          rangeMeta && rangeMeta.propertyId === row.propertyId
                            ? rangeMeta
                            : null
                        }
                        onCellClick={cellClickEstable}
                        onCellDoubleClick={cellDoubleClickEstable}
                        onCellContextMenu={cellContextMenuEstable}
                      />
                    </div>
                  </Fragment>
                );
              })}

              {/* Fila informativa de MERCADO (admin): va SIEMPRE que el switch
                  esté encendido, entre las filas de reserva y la expansión —
                  NO tiene interacción. La expansión (5 filas meta) sigue igual,
                  debajo de esta fila. Round 2026-07-24. */}
              {mercadoEnabled && mercadoPaleta && (
                <FilaMercado
                  nombre={formatPropertyName(rows[0]?.propertyName ?? "")}
                  datos={mercadoByProperty?.[propertyId]}
                  dates={dates}
                  gridTemplateColumns={datesGridTemplateColumns}
                  paleta={mercadoPaleta}
                  staysData={staysData}
                />
              )}

              {/* detalle stays (después de TODAS las filas/tracks de esta propiedad) */}
              {isExpanded && !staysData && (
                <>
                  <Tooltip title="Cargando datos del calendario..." mouseEnterDelay={1}>
                    <div className="cal-prop cal-sticky-col" style={{ padding: "8px 12px", color: "#999", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      Cargando datos del calendario...
                    </div>
                  </Tooltip>
                  <div className="cal-row-dates" />
                </>
              )}
              {isExpanded && staysData && (
                <FilasMetaPropiedad
                  propertyId={propertyId}
                  staysData={staysData}
                  dates={dates}
                  gridTemplateColumns={datesGridTemplateColumns}
                  canEditMeta={canEditMeta}
                  refreshing={metaRefreshingIds?.has(propertyId)}
                  draft={
                    metaDraft && metaDraft.propertyId === propertyId
                      ? metaDraft
                      : null
                  }
                  onMetaCellClick={metaCellClickEstable}
                />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
